import { SortDescriptor } from '@progress/kendo-data-query';
import { formatExportPathname, isEqual, JSONValue } from 'common/components/widgets/utils';
import useWidgetRequestBuilder from 'common/components/widgets/widget-hooks/useWidgetRequestBuilder';
import {
  excelProcessors,
  hasAsyncExport,
} from 'common/components/widgets/widget-utils/widgetUtils';
import { PAGING_OPTIONS, DATASET_INITIAL_ID } from 'common/constants';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { DateFilterTab } from 'common/models/date-filter.model';
import { removeChartFilter, selectChartFilters } from 'common/store/slices/chart-filters.slice';
import { selectOverrideDateFilter } from 'common/store/slices/dashboard-editor.slice';
import { selectPagination } from 'common/store/slices/pagination.slice';
import { selectAllWidgetDataById } from 'common/store/slices/widget-data.slice';
import useUniversalFilters from 'modules/insights/hooks/useUniversalFilters';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { REPORTS_ROUTES } from 'routes/routes.const';
import { AppProject, Claim } from '../models/page-config.models';
import {
  WidgetDataType,
  WidgetModel,
  WidgetRequestWithFilters,
  WidgetType,
} from '../models/widget.model';
import { RootState } from '../store';
import {
  useGetAggregateQuery,
  useGetChartSeriesQuery,
  useGetGeoDataQuery,
  useGetListQuery,
} from '../store/api/chart.api';
import { selectAccessToken } from '../store/slices/auth.slice';
import { addChartFilter } from '../store/slices/chart-filters.slice';
import { selectAppliedFilters } from '../store/slices/common-filters.slice';
import { selectDatasetQuestion } from '../store/slices/dataset.slice';
import {
  selectEndDate,
  selectStartDate,
  selectVisualData,
  selectVisualType,
} from '../store/slices/date-filter.slice';
import { selectPageParams } from '../store/slices/page-composer.slice';
import { selectSearchQuery } from '../store/slices/search.slice';
import { selectWidgetFilters } from '../store/slices/widget-filter.slice';
import { selectWidgetSort } from '../store/slices/widget-sort.slice';
import { transformCommonFiltersForRequest } from './filters';
import useClaims from './useClaims';
import useFilterLabels from './useFilterLabels';
import usePageFilters from './usePageFilters';
import { adjustFilterDates } from './utils';
import { CommonFiltersItem } from 'common/models/filter.model';
import { WidgetExportModel } from 'common/models/export.model';
import {
  removeExportDataForWidget,
  selectExportDataById,
  setExportData,
} from 'common/store/slices/export.slice';
import { FILTER_ITEMS_KEYS } from 'modules/insights/constants/constants-filter-configs';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { onFilterClickCallback } from 'common/models/widget.model';
import {
  useGetEventAttendeesQuery,
  useGetListsQuery,
  useGetProfileActivitiesParticipationQuery,
  useGetProfileDetailsQuery,
  useGetProfileDetailsSummaryQuery,
  useGetProfileInteractionsQuery,
  useGetProfilesQuery,
  useGetProfilesSummaryQuery,
} from 'common/store/api/planning.api';
import { useWidgetOptionsContext } from 'common/contexts/useWidgetOptionsContext';
import useFilterClick from './useFilterCLick';
import { NON_FILTER_GROUPS } from 'common/components/widgets/widget-constants';

export const EXCLUDE_FROM_EXPORTS = [WidgetDataType.LIST];

const StaticQuery = () => {
  return {
    data: {},
    error: null,
    isLoading: false,
    isFetching: false,
  };
};

export const DATA_ENDPOINT_MAP: any = {
  [WidgetDataType.SERIES]: useGetChartSeriesQuery,
  [WidgetDataType.LIST]: useGetListQuery,
  [WidgetDataType.COUNTER]: useGetAggregateQuery,
  [WidgetDataType.ELSIE_AI_V2_COUNTER]: useGetAggregateQuery,
  [WidgetDataType.GEO]: useGetGeoDataQuery,
  [WidgetDataType.SINGLE_METRIC]: useGetAggregateQuery,
  [WidgetDataType.PLANNING_KWELLO_GRID]: useGetEventAttendeesQuery,
  [WidgetDataType.PLANNING_PROFILES_COUNTER]: useGetProfilesSummaryQuery,
  [WidgetDataType.PLANNING_PROFILE_DETAILS_COUNTERS]: useGetProfileDetailsSummaryQuery,
  [WidgetDataType.PLANNING_PROFILES_LIST]: useGetProfilesQuery,
  [WidgetDataType.PLANNING_LISTS]: useGetListsQuery,
  [WidgetDataType.PLANNING_ATTENDEES_LIST]: useGetProfilesQuery,
  [WidgetDataType.PLANNING_PROFILE_DETAILS]: useGetProfileDetailsQuery,
  [WidgetDataType.PLANNING_PROFILE_ACTIVITIES_PARTICIPATION]:
    useGetProfileActivitiesParticipationQuery,
  [WidgetDataType.PLANNING_PROFILE_INTERACTIONS]: useGetProfileInteractionsQuery,
  [WidgetDataType.STATIC]: StaticQuery,
};

export interface WidgetFactoryResult {
  id?: string;
  data: any;
  onSliceClick: (prop: string, value: string | number, displayValue?: string | number) => void;
  currentWidgetGroupBy: any;
  startDate: string;
  endDate: string;
  widgetSort: SortDescriptor[];
  widgetFilter: any;
  exportShadowRequest: any;
  hasData: () => boolean;
  isLoading: boolean;
  isFetching: boolean;
  idToken: string;
  activeProject: AppProject | undefined;
  error: any;
  widgetRequestWithFilters: WidgetRequestWithFilters | null;
  widget: WidgetModel;
  exportData?: WidgetExportModel;
  allDataRequest: any;
  onFilterClick: onFilterClickCallback;
}

export function useWidgetFactory(widget: WidgetModel): WidgetFactoryResult {
  const location = useLocation();
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const { compareUrl } = useCompareUrlWithProjectId();
  const isReportPage = compareUrl(REPORTS_ROUTES.root, { includes: true });
  const searchFilter = useSelector(selectSearchQuery);
  const idToken = useSelector(selectAccessToken);
  const pageParams = useSelector(selectPageParams);
  const widgetFilter = useSelector((state: RootState) => selectWidgetFilters(state, widget.id));

  const widgetSort = useSelector((state: RootState) => selectWidgetSort(state, widget.id));
  const allWidgetData = useSelector((state: RootState) =>
    selectAllWidgetDataById(state, widget.id)
  );
  const { groupBy } = useWidgetOptionsContext();
  const pagination = useSelector((state: RootState) => selectPagination(state, widget.id));
  const { hasClaim } = useClaims();
  const filterLabels = useFilterLabels({ datasetId: widget.datasetId });
  const { universalFilters } = useUniversalFilters();
  const { pageFilterKeys } = usePageFilters({ datasetId: widget.datasetId });
  const storedExportData = useSelector((state: RootState) =>
    selectExportDataById(state, widget.id)
  );

  const reduxStartDate = useSelector((state: RootState) =>
    selectStartDate(state, widget.datasetId ?? DATASET_INITIAL_ID)
  );
  const reduxEndDate = useSelector((state: RootState) =>
    selectEndDate(state, widget.datasetId ?? DATASET_INITIAL_ID)
  );
  const reduxVisualDataType = useSelector((state: RootState) =>
    selectVisualType(state, widget.datasetId ?? DATASET_INITIAL_ID)
  );
  const reduxVisualData = useSelector((state: RootState) =>
    selectVisualData(state, widget.datasetId)
  );

  const { activeProject } = useModuleProjectContext();

  const reduxFilter = useSelector((state: RootState) =>
    selectAppliedFilters(state, widget.datasetId ?? DATASET_INITIAL_ID)
  );
  const chartFilters = useSelector((state: RootState) =>
    selectChartFilters(state, widget.datasetId ?? DATASET_INITIAL_ID)
  );

  const startDate = isReportPage
    ? widget.defaultFilters?.startDate
    : reduxStartDate ?? widget?.defaultFilters?.startDate;
  const endDate = isReportPage
    ? widget.defaultFilters?.endDate
    : reduxEndDate ?? widget?.defaultFilters?.endDate;
  const visualDataType = isReportPage
    ? widget.defaultFilters?.visualDataType
    : reduxVisualDataType ?? widget?.defaultFilters?.visualDataType;
  const visualData = isReportPage
    ? widget.defaultFilters?.visualData
    : reduxVisualData ?? widget?.defaultFilters?.visualData;
  const overrideDateFilter = useSelector(selectOverrideDateFilter);

  const filter = widget?.defaultFilters?.filters ?? reduxFilter;

  const datasetQuestion = useSelector((state: RootState) =>
    selectDatasetQuestion(state, widget.datasetId, projectId ?? '')
  );

  const currentWidgetGroupBy = groupBy ?? widget?.defaultGroupBy ?? null;

  const widgetRequest = useWidgetRequestBuilder({ widget });

  const dateFilters: { startDate: string; endDate: string } = useMemo(() => {
    const start = overrideDateFilter ? universalFilters?.startDate ?? startDate : startDate;
    const end = overrideDateFilter ? universalFilters?.endDate ?? endDate : endDate;
    const type: DateFilterTab = overrideDateFilter
      ? universalFilters?.visualDataType ?? visualDataType
      : visualDataType;
    const visual = overrideDateFilter ? universalFilters?.visualData ?? visualData : visualData;

    return adjustFilterDates({
      startDate: start,
      endDate: end,
      visualData: visual,
      visualType: type,
    });
  }, [
    endDate,
    overrideDateFilter,
    startDate,
    universalFilters?.endDate,
    universalFilters?.startDate,
    universalFilters?.visualData,
    universalFilters?.visualDataType,
    visualData,
    visualDataType,
  ]);

  const validFilters = useMemo(() => {
    const allFilterKeys = isReportPage ? FILTER_ITEMS_KEYS : pageFilterKeys;
    const filterKeys = Object.keys(filter).filter((key) => allFilterKeys.includes(key));

    // If on a report page, return valid filters from the widget default filters.
    if (isReportPage) {
      return Object.keys(widget.defaultFilters?.filters ?? {}).reduce(
        (acc: CommonFiltersItem, key: string) => {
          acc[key] = (filter as CommonFiltersItem)[key];
          return acc;
        },
        {}
      );
    }

    return filterKeys.reduce((acc: CommonFiltersItem, key: string) => {
      acc[key] = (filter as CommonFiltersItem)[key];
      return acc;
    }, {});
  }, [isReportPage, pageFilterKeys, filter, widget.defaultFilters]);

  const staticValidFilters = JSON.stringify(validFilters);

  const filters = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    () => ({
      ...transformCommonFiltersForRequest({
        ...validFilters,
        ...((universalFilters?.filters as CommonFiltersItem) ?? {}),
      }),
      startDate: dateFilters.startDate,
      endDate: dateFilters.endDate,
      searchQuery: searchFilter,
      ...pageParams?.filters,
    }),
    //  - Because validFilters can't be serialized easily
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      universalFilters?.filters,
      dateFilters.startDate,
      dateFilters.endDate,
      searchFilter,
      pageParams?.filters,
      staticValidFilters,
    ]
  );

  const widgetRequestWithFilters: WidgetRequestWithFilters | null = useMemo(() => {
    if (!widgetRequest) {
      return null;
    }

    return {
      filters,
      includeAlignment: hasClaim(Claim.Alignment),
      request: widgetRequest,
      id: widget.id,
      headers: {
        Authorization: 'Bearer ' + idToken,
        ProjectID: activeProject?.moduleProjectID,
      },
    };
  }, [filters, widgetRequest, idToken, activeProject?.moduleProjectID, hasClaim, widget.id]);

  // Having to use any here because the data is not typed properly and would require a lot of work to fix
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  const widgetData = useMemo(() => (widget.request?.data ?? {}) as any, [widget.request?.data]);

  const paginatedWidgetData = useMemo(
    () => ({
      data: pagination
        ? allWidgetData?.data.slice(pagination.skip, pagination.skip + pagination.take)
        : allWidgetData?.data.slice(0, widgetData?.pageSize ?? PAGING_OPTIONS[0]),

      total: allWidgetData?.total,
    }),
    [allWidgetData, pagination, widgetData]
  );

  const { data, error, isLoading, isFetching } = widgetRequest
    ? DATA_ENDPOINT_MAP[widget.dataType](widgetRequestWithFilters, {
        skip: !idToken || idToken == '' || !activeProject?.moduleProjectID,
        refetchOnMountOrArgChange: true,
      })
    : { data: [], error: false, isLoading: false, isFetching: false };

  const allDataRequest = useMemo(() => {
    if (!widgetRequest) {
      return null;
    }

    return {
      ...widgetRequestWithFilters,
      request: {
        ...widgetRequest,
        data: {
          ...widgetRequest.data,
          skip: 0,
          pageSize: 100000,
        },
      },
    };
  }, [widgetRequest, widgetRequestWithFilters]);

  const exportShadowRequest =
    excelProcessors[widget.id] || hasAsyncExport.includes(widget.id)
      ? { data: [], error: false, isLoading: false }
      : DATA_ENDPOINT_MAP[widget.dataType]?.(allDataRequest, {
          skip:
            !idToken ||
            idToken == '' ||
            !activeProject?.moduleProjectID ||
            !widget.excelExportAllData,
          refetchOnMountOrArgChange: !idToken || !activeProject?.moduleProjectID,
        });

  const onSliceClick = useCallback(
    (prop: string, value: string | number, displayValue?: string | number) => {
      if ([''].includes(prop)) {
        // property is empty so ignore it
        return;
      }

      if (NON_FILTER_GROUPS.includes(prop)) {
        // Some groups are not filterable, if we try and filter by one, do not apply the filter
        return;
      }

      if (!isReportPage) {
        const existingFilterValueIndex = chartFilters.findIndex(
          (filter) => filter.propertyName === prop && filter.value.toString() === value.toString()
        );

        if (existingFilterValueIndex > -1) {
          dispatch(
            removeChartFilter({
              datasetId: widget.datasetId ?? DATASET_INITIAL_ID,
              index: existingFilterValueIndex,
              propertyName: prop,
              value: value,
            })
          );
        } else {
          const widgetLabel = widget.labels ? widget.labels[value.toString()] : value.toString();
          dispatch(
            addChartFilter({
              datasetId: widget.datasetId ?? DATASET_INITIAL_ID,
              chartFilter: {
                id: widget.id,
                propertyName: prop,
                propertyDisplayName: widget.labels ? widget.labels[prop] : prop,
                propertyDisplayValue: displayValue !== undefined ? `${displayValue}` : widgetLabel,
                value: value,
              },
            })
          );
        }
      }
    },
    [dispatch, widget.datasetId, widget.id, widget.labels, isReportPage, chartFilters]
  );

  const onFilterClick = useFilterClick({ datasetId: widget.datasetId ?? DATASET_INITIAL_ID });

  const isExportExcluded = (widget: WidgetModel) => {
    if (widget.type === WidgetType.WORD_CLOUD) {
      return false;
    }
    return EXCLUDE_FROM_EXPORTS.includes(widget.dataType);
  };

  const staticWidget = JSON.stringify(widget);

  const exportData = useMemo(
    () =>
      !isExportExcluded(widget) && data !== undefined
        ? {
            pageTitle: widget.pageTitle,
            filters,
            filterLabels,
            widget: {
              ...widget,
              pathname: formatExportPathname(location.pathname),
              datasetQuestion,
            },
            response: {
              ...data,
              image: storedExportData?.response?.image,
            },
          }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      datasetQuestion,
      filterLabels,
      filters,
      location.pathname,
      staticWidget,
      storedExportData,
    ]
  );

  const lastExportRef = useRef<any>();

  useEffect(() => {
    if (
      exportData &&
      (!lastExportRef.current ||
        !isEqual(lastExportRef.current, exportData as unknown as JSONValue))
    ) {
      dispatch(setExportData({ id: widget.id, data: exportData }));
      lastExportRef.current = exportData;
    }
  }, [dispatch, exportData, widget.id]);

  useEffect(() => {
    return () => {
      // Remove widget from exports store when widget is unmounted
      dispatch(removeExportDataForWidget({ id: widget.id }));
    };
  }, [dispatch, widget.id]);

  const hasData = useCallback((): boolean => {
    switch (widget.dataType) {
      case WidgetDataType.LIST:
        return (
          data?.data?.length > 0 ||
          (paginatedWidgetData && (paginatedWidgetData?.data?.length ?? 0) > 0)
        );
      case WidgetDataType.SERIES:
        return (
          !!data?.groupedList?.length &&
          !!data?.groupedList?.some((group: { data: any[] }) => group.data.length > 0)
        );
      case WidgetDataType.COUNTER:
        if (widget.type === WidgetType.COUNTER) {
          return true;
        }

        if (data?.groupedList) {
          return !!data.groupedList.some((group: { data: any[] }) => group.data.length > 0);
        }

        if (data?.data) {
          return data.data.length > 0;
        }

        return true;
      case WidgetDataType.GEO:
        return !!data?.features?.some(
          (feature: { properties: { dataCount: number } }) => feature.properties.dataCount > 0
        );
      default:
        return true;
    }
  }, [data, widget, paginatedWidgetData]);

  return {
    data: allWidgetData !== null ? paginatedWidgetData : data,
    onSliceClick,
    currentWidgetGroupBy,
    startDate,
    endDate,
    widgetSort,
    widgetFilter,
    exportShadowRequest,
    hasData,
    isLoading:
      isLoading || isFetching || exportShadowRequest?.isLoading || exportShadowRequest?.isFetching,
    isFetching,
    idToken,
    activeProject,
    error,
    widget,
    widgetRequestWithFilters,
    exportData,
    allDataRequest,
    onFilterClick,
  };
}
